import React, { memo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import Box from 'ui-box';
import {
  PageContainer,
  Container,
  majorScale,
  Heading,
  minorScale,
  PageLoader,
  VirtualTable,
  VirtualTableItem,
  Spinner,
  Tag,
  Icon,
  Button,
} from '../../npaper';
import { CompanyThumbnail } from '../companies/CompanyThumbnail';
import { useStores } from '../../stores';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import {
  IPurchaseOrder,
  IPurchaseOrderSnapshotOut,
  PurchaseOrder,
  PurchaseOrderStatus,
} from '../../stores/PurchaseOrderStore';
import { observer } from 'mobx-react-lite';
import { Link, useHistory } from 'react-router-dom';
import { PurchaseOrderStatusTag } from './PurchaseOrderStatusTag';
import { FilterPopover } from '../../npaper/FilterPopover';
import { SearchInput } from '../../npaper/SearchInput';
import { PurchaseOrderDetailsDialog } from './PurchaseOrderDetailsDialog';

export const PurchaseOrders = () => {
  const { purchaseOrderStore } = useStores();
  const history = useHistory();
  const { items, isLoading, fetchMore, total, filter, setFilter } =
    useFilterFetch<typeof PurchaseOrder>(purchaseOrderStore as any, {
      ordering: '-created_at,-id',
    });

  const [isCreatingPurchaseOrder, setIsCreatingPurchaseOrder] = useState(false);

  return !items ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      height="100%"
      maxHeight="100vh"
    >
      <PurchaseOrderDetailsDialog
        isShown={isCreatingPurchaseOrder}
        onClose={(po) => {
          setIsCreatingPurchaseOrder(false);
          if (po) history.push(`/purchase-orders/${po.id}`);
        }}
      />
      <Container marginBottom={majorScale(2)} flexGrow={0} flexShrink={0}>
        <Heading size={700} marginBottom={minorScale(3)}>
          Indkøbsordrer
        </Heading>
        <Box display="flex" alignItems="center">
          <SearchInput
            marginRight={majorScale(1)}
            currentQuery={filter.filter ? filter.filter.search : ''}
            onSearch={(query) => {
              setFilter({
                ...filter,
                filter: { ...filter.filter, search: query },
              });
            }}
          />
          <FilterPopover<IPurchaseOrderSnapshotOut>
            filter={filter.filter}
            onChange={(f) => setFilter({ ...filter, filter: f })}
            filterFields={[
              {
                field: 'status',
                label: 'Status',
                type: 'option',
                options: [
                  {
                    id: PurchaseOrderStatus.DRAFT,
                    label: 'Kladde',
                  },
                  {
                    id: PurchaseOrderStatus.OPEN,
                    label: 'Åben',
                  },
                  {
                    id: PurchaseOrderStatus.FINALIZED,
                    label: 'Færdig',
                  },
                ],
              },
              {
                field: 'createdAt',
                label: 'Oprettet',
                type: 'date',
              },
              {
                field: 'supplier',
                label: 'Leverandør',
                type: 'company',
              },
              {
                field: 'costPrice',
                label: 'Kostpris',
                type: 'number',
              },
              {
                field: 'salesPrice',
                label: 'Salgspris',
                type: 'number',
              },
              {
                field: 'tags',
                label: 'Tags',
                type: 'tag',
              },
            ]}
          />
          <Button
            iconLeft="add"
            marginLeft="auto"
            onClick={() => setIsCreatingPurchaseOrder(true)}
          >
            <FormattedMessage defaultMessage="Create purchase order" />
          </Button>
        </Box>
      </Container>

      <VirtualTable
        isLoading={isLoading}
        items={items}
        total={total}
        rowHeight={48}
        ordering={filter.ordering}
        onOrderingChange={(ordering) => setFilter({ ...filter, ordering })}
        noItemsPlaceholder={
          <Box width="100%" textAlign="center" paddingY={majorScale(6)}>
            <Heading size={500} marginBottom={majorScale(1)}>
              <FormattedMessage defaultMessage="No matching purchase orders found" />
            </Heading>
            <p>
              <FormattedMessage
                defaultMessage="Try changing your filters or <link>see all your purchase orders</link>"
                values={{
                  link: (chunk: string) => (
                    <span
                      className="link medium"
                      onClick={() =>
                        setFilter({ filter: {}, ordering: filter.ordering })
                      }
                    >
                      {chunk}
                    </span>
                  ),
                }}
              />
            </p>
          </Box>
        }
        header={[
          {
            title: 'Indkøbsordre',
            width: 160,
            orderByKeys: ['purchase_order_number', 'id'],
          },
          { title: 'Leverandør', orderByKeys: ['supplier__name', 'id'] },
          { title: 'Status', orderByKeys: ['status', 'id'], width: 180 },
          { title: 'Reference', orderByKeys: ['reference', 'id'] },
          { title: 'Oprettet', orderByKeys: ['created_at', 'id'], width: 180 },
          {
            title: 'Salgspris',
            orderByKeys: ['sales_price', 'id'],
            width: 180,
            align: 'right',
          },
          {
            title: 'Kostpris',
            orderByKeys: ['cost_price', 'id'],
            width: 180,
            align: 'right',
          },
          { title: 'Tags', orderByKeys: ['tags__name', 'id'] },
        ]}
        onRequestMore={async () => fetchMore()}
        renderRow={({ item }) => <MemoizedRow purchaseOrder={item} />}
      />
    </PageContainer>
  );
};

const MemoizedRow = memo(
  observer(({ purchaseOrder }: { purchaseOrder: IPurchaseOrder }) => (
    <>
      <VirtualTableItem className="medium font-mono">
        <Link className="a" to={`/purchase-orders/${purchaseOrder.id}`}>
          <Box is="span" display="inline-flex" alignItems="center">
            <Icon icon="truck" marginRight={majorScale(1)} />
            <span>{purchaseOrder.id}</span>
          </Box>
        </Link>
      </VirtualTableItem>
      <VirtualTableItem>
        {purchaseOrder.supplier ? (
          <CompanyThumbnail company={purchaseOrder.supplier} />
        ) : (
          <Spinner size={16} />
        )}
      </VirtualTableItem>
      <VirtualTableItem>
        <PurchaseOrderStatusTag small={true} purchaseOrder={purchaseOrder} />
        {purchaseOrder.drop_shipping && (
          <Tag intent="warning" small>
            <FormattedMessage defaultMessage="Drop shipping" />
          </Tag>
        )}
      </VirtualTableItem>
      <VirtualTableItem className="font-mono">
        {purchaseOrder.reference}
      </VirtualTableItem>
      <VirtualTableItem>
        <FormattedDate value={purchaseOrder.created_at} format="default" />
      </VirtualTableItem>
      <VirtualTableItem className="font-mono" textAlign="right">
        <FormattedNumber
          format="currency"
          value={purchaseOrder.sales_price || 0}
        />
      </VirtualTableItem>
      <VirtualTableItem className="font-mono" textAlign="right">
        <FormattedNumber format="currency" value={purchaseOrder.cost_price} />
      </VirtualTableItem>
      <VirtualTableItem>
        {purchaseOrder.tags.map((t) => (
          <Tag key={t} small={true} marginRight={minorScale(1)} intent="info">
            {t}
          </Tag>
        ))}
      </VirtualTableItem>
    </>
  ))
);
